import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import { navigateToQuoteForm } from '../utils/quoteNavigation';
import { business, colors, layout } from '../theme/tokens';
import Logo from './ui/Logo';

const serviceItems = [
  { label: 'Plumbing', to: '/plumbing', description: 'Repairs, installs & drains' },
  { label: 'Renovations', to: '/renovations', description: 'Kitchens, bathrooms & whole homes' },
  { label: 'Maintenance', to: '/maintenance', description: 'Ongoing property care' },
];

const primaryNav = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
];

const secondaryNav = [
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Reviews', to: '/testimonials' },
  { label: 'Locations', to: '/locations' },
  { label: 'Contact', to: '/contact' },
];

const Navbar = () => {
  const location = useLocation();
  const history = useHistory();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesAnchor, setServicesAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setServicesAnchor(null);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const servicesActive = serviceItems.some((s) => isActive(s.to));

  const handleQuote = () => {
    setDrawerOpen(false);
    navigateToQuoteForm(history, location);
  };

  const navLinkSx = (active: boolean) => ({
    position: 'relative',
    px: 1.5,
    py: 1,
    minWidth: 0,
    fontSize: '0.92rem',
    fontWeight: 500,
    color: active ? colors.white : colors.textMuted,
    borderRadius: '8px',
    '&:hover': { color: colors.white, backgroundColor: 'rgba(255,255,255,0.06)' },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 12,
      right: 12,
      bottom: 4,
      height: 2,
      borderRadius: '2px',
      backgroundColor: colors.accent,
      transform: active ? 'scaleX(1)' : 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform .25s ease',
    },
  });

  const renderLink = (item: { label: string; to: string }) => (
    <Button
      key={item.to}
      component={RouterLink}
      to={item.to}
      disableRipple
      aria-current={isActive(item.to) ? 'page' : undefined}
      sx={navLinkSx(isActive(item.to))}
    >
      {item.label}
    </Button>
  );

  const desktopNav = (
    <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5, ml: 'auto' }}>
      {primaryNav.map(renderLink)}
      <Button
        disableRipple
        aria-haspopup="menu"
        aria-expanded={Boolean(servicesAnchor)}
        aria-controls="services-menu"
        onClick={(e) => setServicesAnchor(e.currentTarget)}
        onMouseEnter={(e) => setServicesAnchor(e.currentTarget)}
        endIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 18, transition: 'transform .2s', transform: servicesAnchor ? 'rotate(180deg)' : 'none' }} />}
        sx={{ ...navLinkSx(servicesActive), '& .MuiButton-endIcon': { ml: 0.25 } }}
      >
        Services
      </Button>
      <Menu
        id="services-menu"
        anchorEl={servicesAnchor}
        open={Boolean(servicesAnchor)}
        onClose={() => setServicesAnchor(null)}
        MenuListProps={{ onMouseLeave: () => setServicesAnchor(null), sx: { py: 1, minWidth: 260 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {serviceItems.map((item) => (
          <MenuItem
            key={item.to}
            component={RouterLink}
            to={item.to}
            selected={isActive(item.to)}
            onClick={() => setServicesAnchor(null)}
            sx={{ px: 2, py: 1.25, alignItems: 'flex-start', flexDirection: 'column', gap: 0.25 }}
          >
            <Typography sx={{ fontWeight: 600, color: colors.text, fontSize: '0.95rem' }}>{item.label}</Typography>
            <Typography sx={{ color: colors.textSubtle, fontSize: '0.8rem' }}>{item.description}</Typography>
          </MenuItem>
        ))}
      </Menu>
      {secondaryNav.map(renderLink)}
      <Button variant="contained" size="small" onClick={handleQuote} sx={{ ml: 1.5, px: 2.5, py: 1.1 }}>
        Get a quote
      </Button>
    </Box>
  );

  const drawer = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sx={{ zIndex: 1400 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, borderLeft: `1px solid ${colors.border}` } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, height: layout.navHeight }}>
        <Logo height={34} />
        <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close menu">
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ px: 1.5, py: 2 }} component="nav" aria-label="Mobile">
        {[...primaryNav, ...secondaryNav].map((item) => (
          <ListItemButton
            key={item.to}
            component={RouterLink}
            to={item.to}
            selected={isActive(item.to)}
            sx={{ borderRadius: '10px', mb: 0.5, py: 1.4, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}
          >
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: '1.05rem' }} />
          </ListItemButton>
        ))}
        <Typography variant="overline" component="p" sx={{ color: colors.textSubtle, px: 2, pt: 2, pb: 1 }}>
          Services
        </Typography>
        {serviceItems.map((item) => (
          <ListItemButton
            key={item.to}
            component={RouterLink}
            to={item.to}
            selected={isActive(item.to)}
            sx={{ borderRadius: '10px', mb: 0.5, py: 1.2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}
          >
            <ListItemText
              primary={item.label}
              secondary={item.description}
              primaryTypographyProps={{ fontWeight: 600 }}
              secondaryTypographyProps={{ sx: { color: colors.textSubtle } }}
            />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ mt: 'auto', p: 2.5, display: 'grid', gap: 1.5 }}>
        <Button variant="contained" size="large" fullWidth onClick={handleQuote}>
          Get a free quote
        </Button>
        <Button variant="outlined" size="large" fullWidth href={business.phoneHref} startIcon={<PhoneIcon />}>
          {business.phoneDisplay}
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 1 }}>
          <IconButton component="a" href={business.whatsappHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
            <WhatsAppIcon />
          </IconButton>
          <IconButton component="a" href={business.instagramHref} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <InstagramIcon />
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled ? 'rgba(17,17,17,0.82)' : 'transparent',
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          borderBottom: `1px solid ${scrolled ? colors.border : 'transparent'}`,
          transition: 'background-color .3s ease, border-color .3s ease',
          backgroundImage: 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            component="nav"
            aria-label="Primary"
            sx={{ minHeight: `${scrolled ? layout.navHeightCompact : layout.navHeight}px !important`, transition: 'min-height .3s ease', gap: 2 }}
          >
            <Logo height={scrolled ? 34 : 40} priority />
            {desktopNav}
            <Box sx={{ display: { xs: 'flex', lg: 'none' }, ml: 'auto', alignItems: 'center', gap: 1 }}>
              <Button variant="contained" size="small" onClick={handleQuote} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                Get a quote
              </Button>
              <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open menu" aria-expanded={drawerOpen}>
                <MenuRoundedIcon sx={{ fontSize: 30 }} />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {drawer}
      <Box sx={{ height: layout.navHeight }} aria-hidden />
    </>
  );
};

export default Navbar;
